import {
  Module,
  VideoActivityData,
  MCQActivityData,
  EnglishSentenceData,
  FillInTheBlankData,
  ListeningData,
  BuildSentenceData,
  MatchData,
  CelebrateData,
} from "../activityTypes";
import {
  M1_INTRO_VIDEO_LESSON_ID,
  M1_CION_RULE_LESSON_ID,
  M1_ANTE_CONTEXT_LESSON_ID,
  M1_FILL_COMUNICACION_LESSON_ID,
  M1_FILL_NACION_LESSON_ID,
} from "../flightSchoolContent";
import { CION_WORDS, ANTE_WORDS } from "./words";

// =============================================================================
// Module 1 Activities (Cognates 1 Revamp v4)
// 20 steps: INTRO + 16 activities + 3 celebrations
// =============================================================================

// --- INTRO -- VIDEO (Video 1: What are cognates?) ---
// One-time only -- plays once at the very start

const m1IntroVideo: VideoActivityData = {
  id: "m1-intro-video",
  type: "video",
  lessonId: M1_INTRO_VIDEO_LESSON_ID,
  isOneTimeIntro: true,
};

// --- Step 1 -- ES (introActivity) ---

const m1Step1_ES: EnglishSentenceData = {
  id: "m1-step1",
  type: "english_sentence",
  sentence:
    "I need this {aplicación} because my {comunicación} skills are not it.",
  cognateWords: [CION_WORDS[5], CION_WORDS[0]], // aplicación, comunicación
};

// --- Step 2 -- VIDEO (Video 2: The CION rule) ---

const m1Step2_Video: VideoActivityData = {
  id: "m1-step2-video",
  type: "video",
  lessonId: M1_CION_RULE_LESSON_ID,
};

// --- Step 3 -- MCQ ---

const m1Step3_MCQ: MCQActivityData = {
  id: "m1-step3-mcq",
  type: "mcq",
  question: "education",
  options: [
    { choice: "aplicación", correct: false },
    { choice: "información", correct: false },
    { choice: "comunicación", correct: false },
    { choice: "educación", correct: true },
  ],
  isReverse: true,
};

// --- Step 4 -- ES ---

const m1Step4_ES: EnglishSentenceData = {
  id: "m1-step4",
  type: "english_sentence",
  sentence:
    "She has {información} that's apparently very {importante} but she won't text back.",
  cognateWords: [CION_WORDS[1], ANTE_WORDS[0]], // información, importante
};

// --- Step 5 -- FILL IN THE BLANK ---

const m1Step5_Fill: FillInTheBlankData = {
  id: "m1-step5-fill",
  type: "fill_in_the_blank",
  lessonId: M1_FILL_COMUNICACION_LESSON_ID,
  spanishSentence: "La ___ es buena",
  englishHint: "The communication is good.",
  correctWord: CION_WORDS[0], // comunicación
  incorrectWord: CION_WORDS[1], // información
  extraIncorrectWords: [CION_WORDS[4], CION_WORDS[3]], // educación, nación
  showWordHints: true,
};

// --- Step 6 -- LISTEN (4 options, all -ción) ---

const m1Step6_Listen: ListeningData = {
  id: "m1-step6-listen",
  type: "listening",
  targetWord: CION_WORDS[2], // situación
  distractorWord: CION_WORDS[3], // nación
  additionalDistractors: [CION_WORDS[0], CION_WORDS[1]], // comunicación, información
};

// --- Step 7 -- CELEBRATE (Section 1 complete) ---

const m1Step7_Celebrate: CelebrateData = {
  id: "m1-step7-celebrate",
  type: "celebrate",
  patternLabel: "-CIÓN = -TION",
  words: [
    CION_WORDS[0], // comunicación
    CION_WORDS[5], // aplicación
    CION_WORDS[4], // educación
    CION_WORDS[1], // información
    ANTE_WORDS[0], // importante
    CION_WORDS[2], // situación
  ],
};

// --- Step 8 -- ES ---

const m1Step8_ES: EnglishSentenceData = {
  id: "m1-step8",
  type: "english_sentence",
  sentence:
    "He showed up to the {restaurante} looking all {elegante} and I was in sweatpants.",
  cognateWords: [ANTE_WORDS[1], ANTE_WORDS[2]], // restaurante, elegante
};

// --- Step 9 -- VIDEO (Video 4: The ANTE rule) ---

const m1Step9_Video: VideoActivityData = {
  id: "m1-step9-video",
  type: "video",
  lessonId: M1_ANTE_CONTEXT_LESSON_ID,
};

// --- Step 10 -- BUILD SENTENCE ---

const m1Step10_Build: BuildSentenceData = {
  id: "m1-step10-build",
  type: "build_sentence",
  correctSentence: "La educación es importante",
  englishHint: "Education is important",
  words: ["importante", "educación", "es", "La"],
  cognateWord: CION_WORDS[4], // educación
  showEnglishHints: true,
};

// --- Step 11 -- LISTEN (4 options, all -ante) ---

const m1Step11_Listen: ListeningData = {
  id: "m1-step11-listen",
  type: "listening",
  targetWord: ANTE_WORDS[3], // interesante
  distractorWord: ANTE_WORDS[2], // elegante
  additionalDistractors: [ANTE_WORDS[0], ANTE_WORDS[4]], // importante, constante
};

// --- Step 12 -- MATCH ---

const m1Step12_Match: MatchData = {
  id: "m1-step12-match",
  type: "match",
  pairs: [
    CION_WORDS[0], // comunicación / communication
    CION_WORDS[1], // información / information
    ANTE_WORDS[1], // restaurante / restaurant
    ANTE_WORDS[2], // elegante / elegant
    CION_WORDS[2], // situación / situation
  ],
};

// --- Step 13 -- CELEBRATE (Section 2 complete) ---

const m1Step13_Celebrate: CelebrateData = {
  id: "m1-step13-celebrate",
  type: "celebrate",
  patternLabel: "-ANTE = -ANT",
  words: [
    CION_WORDS[0], // comunicación
    CION_WORDS[5], // aplicación
    CION_WORDS[4], // educación
    CION_WORDS[1], // información
    ANTE_WORDS[0], // importante
    CION_WORDS[2], // situación
    ANTE_WORDS[1], // restaurante
    ANTE_WORDS[2], // elegante
    ANTE_WORDS[3], // interesante
  ],
};

// --- Step 14 -- ES ---

const m1Step14_ES: EnglishSentenceData = {
  id: "m1-step14",
  type: "english_sentence",
  sentence:
    "The entire {nación} has a {constante} need for tacos and honestly same.",
  cognateWords: [CION_WORDS[3], ANTE_WORDS[4]], // nación, constante
};

// --- Step 15 -- MCQ ---

const m1Step15_MCQ: MCQActivityData = {
  id: "m1-step15-mcq",
  type: "mcq",
  question: "constant",
  options: [
    { choice: "importante", correct: false },
    { choice: "elegante", correct: false },
    { choice: "interesante", correct: false },
    { choice: "constante", correct: true },
  ],
  isReverse: true,
};

// --- Step 16 -- FILL IN THE BLANK ---

const m1Step16_Fill: FillInTheBlankData = {
  id: "m1-step16-fill",
  type: "fill_in_the_blank",
  lessonId: M1_FILL_NACION_LESSON_ID,
  spanishSentence: "La ___ de Colombia es muy interesante",
  englishHint: "The nation of Colombia is very interesting.",
  correctWord: CION_WORDS[3], // nación
  incorrectWord: CION_WORDS[2], // situación
  extraIncorrectWords: [CION_WORDS[0], CION_WORDS[5]], // comunicación, aplicación
  showWordHints: true,
  wordHints: { de: "of", muy: "very", interesante: "interesting" },
};

// --- Step 17 -- BUILD SENTENCE ---

const m1Step17_Build: BuildSentenceData = {
  id: "m1-step17-build",
  type: "build_sentence",
  correctSentence: "La aplicación tiene información importante",
  alternativeCorrectSentences: ["La aplicación tiene importante información"],
  englishHint: "The app has important information",
  words: ["importante", "tiene", "La", "aplicación", "información"],
  cognateWord: CION_WORDS[5], // aplicación
  showEnglishHints: true,
  wordHints: { tiene: "has" },
};

// --- Step 18 -- MATCH ---

const m1Step18_Match: MatchData = {
  id: "m1-step18-match",
  type: "match",
  pairs: [
    CION_WORDS[5], // aplicación / application
    CION_WORDS[4], // educación / education
    CION_WORDS[3], // nación / nation
    ANTE_WORDS[3], // interesante / interesting
    ANTE_WORDS[4], // constante / constant
  ],
};

// --- Step 19 -- CELEBRATE (Final -- Graduate) ---

const m1Step19_Celebrate: CelebrateData = {
  id: "m1-step19-celebrate",
  type: "celebrate",
  title: "Chapter Complete!",
  isFinal: true,
  words: [...CION_WORDS, ...ANTE_WORDS],
  patternLabels: ["-CIÓN = -TION", "-ANTE = -ANT"],
  totalWordsUnlocked: "1000+",
};

// =============================================================================
// Module 1 Definition
// =============================================================================

export const MODULE_1: Module = {
  id: "module-1",
  name: "Cognate Patterns: CION & ANTE",
  patterns: ["CION", "ANTE"],
  activities: [
    // INTRO -- Video 1 (one-time)
    m1IntroVideo,
    // Section 1: CION Pattern (Steps 1-7)
    m1Step1_ES,       // Step 1: ES
    m1Step2_Video,    // Step 2: VIDEO (CION rule)
    m1Step3_MCQ,      // Step 3: MCQ
    m1Step4_ES,       // Step 4: ES
    m1Step5_Fill,     // Step 5: FILL IN THE BLANK
    m1Step6_Listen,   // Step 6: LISTEN (4 options)
    m1Step7_Celebrate, // Step 7: CELEBRATE
    // Section 2: ANTE Pattern (Steps 8-13)
    m1Step8_ES,       // Step 8: ES
    m1Step9_Video,    // Step 9: VIDEO (ANTE rule)
    m1Step10_Build,   // Step 10: BUILD SENTENCE
    m1Step11_Listen,  // Step 11: LISTEN (4 options)
    m1Step12_Match,   // Step 12: MATCH
    m1Step13_Celebrate, // Step 13: CELEBRATE
    // Section 3: Mixed Review (Steps 14-19)
    m1Step14_ES,      // Step 14: ES
    m1Step15_MCQ,     // Step 15: MCQ
    m1Step16_Fill,    // Step 16: FILL IN THE BLANK
    m1Step17_Build,   // Step 17: BUILD SENTENCE
    m1Step18_Match,   // Step 18: MATCH
    m1Step19_Celebrate, // Step 19: CELEBRATE (Final)
  ],
};
